// Talks to the Joy Phone server. The session lives in an HttpOnly cookie, so nothing secret is kept in the page.

export interface User {
  id: number;
  name: string;
  email: string;
  canCall: boolean;
  role: "admin" | "member";
  /** Do Not Disturb: not rung for new calls. */
  dnd?: boolean;
  /** Admins only (1.10.28): how many devices this teammate gets alerts on (0 = none: blocked or never turned on). */
  alertDevices?: number;
  /** Only on my own user (GET/PATCH /me): "ring my cell too" number (E.164) or null. */
  forwardNumber?: string | null;
  /** Only on my own user: my voicemail greeting (used when a caller asked for me), or null = team greeting. */
  voicemailGreeting?: string | null;
  /** "never" | "always" (cell rings with the app) | "dnd" (cell rings instead, while on Do Not Disturb). */
  forwardMode?: "never" | "always" | "dnd";
  vmGreeting?: string | null;
  /** Only on my own user: connect calls via the app (internet) or my cell. */
  callVia?: "app" | "cell";
  /** What forwarded calls show on my cell: the customer's number (name from Joy Phone contacts) or the team number. */
  cellCallerId?: "customer" | "team";
  /** Text my cell "Scott Smith – Joy Phone" as it rings. */
  cellText?: boolean;
}
/** "canceled": a teammate hung up their own outgoing call before the customer answered. */
export type CallStatus = "ringing" | "active" | "completed" | "missed" | "voicemail" | "info" | "canceled";
/** A group text's members (number and saved name). */
export interface GroupMember {
  number: string;
  name: string | null;
}
export interface Conversation {
  /** Group texts: who's in it. */
  members?: GroupMember[];
  /** Group texts: the name the team gave it, if any. */
  groupName?: string | null;
  external_number: string;
  activity_at: string;
  last_kind: "call" | "sms" | "comment";
  last_call_id: string | null;
  direction: "inbound" | "outbound" | null;
  status: CallStatus | null;
  started_at: string | null;
  call_count: number;
  comment_count: number;
  /** A customer text / voicemail or a teammate's comment you haven't seen: counts on the inbox badge. */
  needs_you?: number;
  voicemail_count: number;
  contact_name: string | null;
  /** Caller ID name from a lookup (CNAM). Show only when contact_name is null. */
  caller_name?: string | null;
  /** Latest AI summary (or transcript) for the last call, used as the list preview. */
  preview: string | null;
  teammate_name: string | null;
  /** The last call was the AI agent's own (show its chosen name). */
  teammate_ai?: number | null;
  talk_secs: number | null;
  sms_body: string | null;
  sms_direction: "inbound" | "outbound" | null;
  sms_status: string | null;
  sms_user_name: string | null;
  comment_body: string | null;
  comment_user_name: string | null;
  conv_status: "open" | "done";
  assigned_to: number | null;
  assigned_name: string | null;
  unread: boolean;
  /** I pinned this conversation (per teammate). */
  pinned: boolean;
  /** The customer's latest text / missed call / voicemail hasn't been answered by a text or call yet. */
  unresponded: boolean;
  /** Direction / status / teammate of the newest item (call or text). */
  last_direction: "inbound" | "outbound" | null;
  last_status: string | null;
  last_user_name: string | null;
  /** 9.11.2: a merged contact's numbers (their conversations are one card). */
  numbers?: string[];
}
export type VoiceKey = "ava" | "andrew" | "danielle" | "emma" | "brian" | "jenny" | "gregory";
export interface StorageInfo {
  totalBytes: number;
  freeBytes: number;
  usedBytes: number;
  usedPct: number;
}
export interface Billing {
  month: string;
  updatedAt?: string;
  from: string;
  to: string;
  balance: { balance: string; available_credit: string; credit_limit: string; pending: string; currency: string } | null;
  total: number;
  products: { product: string; label: string; cost: number; parts: { label: string; cost: number }[] }[];
  failed: string[];
  invoices: { invoice_id: string; period_start: string; period_end: string; paid: boolean; url: string }[];
  /** AI charges, itemized (by model / voice / assistant), and what Joy did with AI that month. */
  ai?: {
    total: number;
    products: { product: string; label: string; cost: number; parts: { label: string; cost: number }[] }[];
    activity: { aiCalls: number; aiMinutes: number; summaries: number; transcribedMinutes: number; chatReplies: number };
  };
}
export interface SelenaSettings {
  voice: string;
  /** Who answers and calls for Joy Travel (always matches the voice). */
  persona: "selena" | "steven";
  /** The agent's name with a woman's voice / a man's voice (Selena / Steven by default). */
  nameWoman?: string;
  nameMan?: string;
  /** 13.10.20: the AI model that thinks on her calls. */
  phoneModel?: string;
  hearing?: string;
  chatBrain?: "deepseek" | "hybrid";
  webBrain?: "claude" | "deepseek";
  /** 6.10.29: which browser runs website tasks (Settings → Browser). */
  webBrowser?: "auto" | "browserbase" | "kernel" | "steel" | "browseruse" | "own";
  /** 1.10.30: Kernel's browser options (left out = Kernel's default). */
  kernel?: KernelOptions;
  /** 1.10.30: Browserbase's browser options (left out = Browserbase's default). */
  browserbase?: BrowserbaseOptions;
  /** Facts about Joy Travel she may use. */
  about: string;
  language: string;
  speed: number;
  allowBooking: boolean;
  askTeam: boolean;
  holdMinutes: number;
  officeSound: boolean;
  /** Telnyx Ultra voices: expressive mode. */
  expressive: boolean;
  /** One rule per line. */
  canSay: string;
  cannotSay: string;
  relaxed: boolean;
  hiddenVoices: string[];
  /** 12.10.30: voices marked ♥ (shown first). */
  favoriteVoices: string[];
  answerCallbacks: boolean;
  webBrowsing: boolean;
  webBooking: boolean;
  webPractice: boolean;
  webIdentity: boolean;
  webPhoneFallback: boolean;
  webHandoff: boolean;
  leaveVoicemail: boolean;
}
/** Selena is waiting for you to do one step in her browser (a security check, a password, a code). */
export interface PendingTakeover { token: string; where: string; kind: "check" | "login" | "code" | "other" | "watch"; reason: string; expiresAt: string }
export interface SelenaVoice { key: string; name: string; gender: "woman" | "man"; sub: string; lang: string; family: "Azure" | "Kokoro" | "Ultra" | "NaturalHD" | "Natural" | "Inworld"; sample: string | null; pick?: boolean; /** How human it sounds, 0–100 (most human-like first; under 50 can sound robotic). */ human?: number }
/** 1.10.30: Browserbase's options for the browsers it runs (see callflow.ts BrowserbaseOptions). */
export interface BrowserbaseOptions {
  verified?: boolean;
  region?: "us-west-2" | "us-east-1" | "eu-central-1" | "ap-southeast-1";
  timeoutMinutes?: number;
  keepAlive?: boolean;
  blockAds?: boolean;
  recordSession?: boolean;
  logSession?: boolean;
  os?: "windows" | "mac" | "linux" | "mobile" | "tablet";
  viewport?: "1920x1080" | "1440x900" | "1280x800" | "1024x768" | "390x844";
  contextId?: string;
  persistContext?: boolean;
}
/** 1.10.30: Kernel's options for the browsers it runs (see callflow.ts KernelOptions). */
export interface KernelOptions {
  headless?: boolean;
  idleMinutes?: number;
  region?: "us-east" | "us-west" | "eu-west" | "ap-southeast";
  timezone?: string;
  locale?: string;
  viewport?: "1920x1080" | "1440x900" | "1280x800" | "1024x768" | "390x844";
  memory?: "1GiB" | "2GiB" | "4GiB" | "8GiB" | "16GiB";
  gpu?: boolean;
  kiosk?: boolean;
  profile?: string;
  saveProfile?: boolean;
  /** Approved bot identity (Web Bot Auth): the Kernel extension that signs requests, by name. */
  identityExtension?: string;
}
export interface SelenaSettingsBody { phoneModels?: { id: string; label: string; note: string }[]; phoneModelState?: string | null; hearingModels?: { id: string; label: string; note: string }[]; hearingState?: string | null; settings: SelenaSettings; voices: SelenaVoice[]; hidden?: SelenaVoice[]; languages: { key: string; label: string }[]; browser?: "ready" | "down" | "missing"; browsers?: { browserbase: boolean; kernel: boolean; steel: boolean; browseruse?: boolean } | null; browserRank?: { browser: "browserbase" | "kernel" | "steel" | "browseruse" | "own"; runs: number; done: number; medianMs: number | null }[]; chatModel?: string | null; smartModel?: string | null }
export interface AgentMessage {
  id: number;
  role: "me" | "agent" | "call";
  body: string;
  /** The AI call this message reports on. */
  call_id: string | null;
  created_at: string;
  /** Files and pictures sent to Selena with it. */
  media?: Attachment[];
  /** A reply to an earlier message in this chat. */
  reply_to?: number | null;
  reply_body?: string | null;
  reply_role?: "me" | "agent" | null;
}
/** Settings → Phone numbers. */
export interface NumbersBody {
  office: string;
  officeSecret: string;
  textProfileKnown: boolean;
  numbers: { id: string; number: string; status: string; calls: boolean; texts: boolean; role: "office" | "personal" | "taken" | "previous" | null }[];
  porting: { number: string; status: string; focDate: string | null }[];
}
/** Selena in Personal mode: each teammate's own, private settings. */
/** 13.10.30 Compare browsers (see src/browserbench.ts). */
export interface BenchTask { url: string; goal: string }
export type BenchBrowser = "own" | "browserbase" | "kernel" | "steel" | "browseruse";
export interface BenchRow { task: number; browser: BenchBrowser; result: "ok" | "stuck" | "refused" | "failed" | "didnt_start" | "stopped"; ms: number; openMs: number | null; steps: number; summary: string; shot?: string | null }
export interface BenchState { running: boolean; startedAt: string | null; finishedAt: string | null; by: string | null; browsers: BenchBrowser[]; tasks: BenchTask[]; rows: BenchRow[]; current: { task: number; browser: BenchBrowser } | null }
export interface PaSettings {
  mode: "business" | "personal";
  name: string;
  number: string;
  callerId: boolean;
  about: string;
  canSay: string;
  cannotSay: string;
  voicemail: string;
  vibe: "professional" | "friendly" | "cheeky";
  /** Who answers and calls for you personally ("same": as on the Joy Travel side). */
  persona: "same" | "selena" | "steven";
  /** Your own voice for personal calls ("": the same as Joy Travel). */
  voice: string;
  /** 12.10.30: your own ♥ voices and removed voices (separate from Joy Travel's). */
  favoriteVoices: string[];
  hiddenVoices: string[];
}
/** One of the AI agent's calls (AI agent screen). */
export interface AiAgentCall {
  id: string;
  external_number: string;
  contact_name: string | null;
  status: CallStatus;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  initiated_by_name: string | null;
  /** A personal-assistant call: only you see it. */
  personal?: boolean;
  task: { hotel: string | null; request: string; details: string | null } | null;
  summary: string | null;
  lastLine: { role: "ai" | "them"; text: string } | null;
}
export interface LiveCall {
  id: string;
  /** An AI call (no teammate on it). */
  ai?: boolean;
  direction: "inbound" | "outbound";
  external_number: string;
  status: CallStatus;
  stage: string | null;
  ring_user: number | null;
  answered_by: number | null;
  answered_by_name: string | null;
  /** When it was answered (server time, UTC) and who placed it (outgoing calls). */
  answered_at?: string | null;
  initiated_by?: number | null;
  contact_name: string | null;
  /** Caller ID name from a lookup (CNAM), if one has finished. */
  caller_name?: string | null;
  on_hold: number;
  // Advanced calls (CALLS_API.md). Always sent by the server; optional so older code still compiles.
  /** The call was upgraded to a conference (listen in, 3-way or warm transfer happened). */
  conference?: boolean;
  /** Admins listening in. */
  monitors?: { userId: number; name: string; mode: MonitorMode; state: "ringing" | "answered" }[];
  /** People added to the call (teammate or outside number). `id` is used to remove them. */
  participants?: { id: string; userId: number | null; name: string | null; number: string | null; state: "ringing" | "answered" }[];
  /** Warm transfer in progress: the caller is on hold while `byUserId` talks with the target. */
  consult?: { userId: number | null; name: string | null; number: string | null; state: "ringing" | "answered"; byUserId: number | null } | null;
  /** Ringing / connected to an outside number (forward route or no-answer action). */
  forwardedTo?: string | null;
}
export type MonitorMode = "listen" | "whisper" | "barge";
/** Who to add or consult: a teammate (rings their app) or an outside number (E.164 or 10-digit US). */
export type CallTarget = { userId: number } | { number: string };
export interface Recording {
  id: number;
  kind: "call" | "voicemail";
  durationSecs: number | null;
  transcript: string | null;
  summary: string | null;
  aiStatus: "pending" | "done" | "failed" | "off";
  hasAudio: boolean;
  /** Transcript lines with their time in the audio (seconds), when known. */
  segments?: { s: number; e: number; t: string; sp?: string }[] | null;
}
export interface Reaction {
  emoji: string;
  users: { id: number; name: string }[];
}
export interface Note {
  /** 1: written by Selena. */
  bot?: number;
  /** The conversation it belongs to. */
  external_number: string;
  anchor: string | null;
  reactions: Reaction[];
  id: number;
  user_id: number;
  user_name: string;
  body: string;
  reply_to: number | null;
  reply_body: string | null;
  reply_user_name: string | null;
  created_at: string;
  /** Set once the author edits it. */
  edited_at?: string | null;
  /** Photos / PDFs dropped or pasted into it (3.10.22). */
  media?: Attachment[];
}
export interface Text {
  /** Group texts: the member who wrote it (their number). */
  sender?: string | null;
  reactions: Reaction[];
  id: number;
  direction: "inbound" | "outbound";
  body: string;
  media: Attachment[];
  status: string;
  error: string | null;
  user_id: number | null;
  user_name: string | null;
  created_at: string;
  /** "rcs" when it went by RCS. */
  channel?: string | null;
  /** RCS read receipt: when the customer read it. */
  read_at?: string | null;
}
export interface Attachment {
  url: string;
  type: string;
  name?: string;
}
export interface ThreadCall {
  /** The number this call was with (a merged contact has several). */
  external_number?: string;
  /** A personal-assistant call (only you see it). */
  private_to?: number | null;
  id: string;
  direction: "inbound" | "outbound";
  status: CallStatus;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  answered_by_name: string | null;
  initiated_by_name: string | null;
  initiated_by?: number | null;
  answered_by?: number | null;
  recordings: Recording[];
  /** AI calls: what it was asked, and what was said so far (live while it talks). */
  aiTask?: { hotel: string | null; request: string; details: string | null } | null;
  aiLog?: { role: "ai" | "them"; text: string }[] | null;
  /** 4.10.22: a customer call's live transcript ("them": the customer; "us": a teammate), as they talk. */
  liveLog?: LiveLine[] | null;
  /** 1 while the caller is on hold (an AI call: waiting for the team's answer). */
  on_hold?: number;
}
export interface LiveLine { who: "them" | "us"; name?: string; text: string; partial?: boolean }
export interface Thread {
  /** Group texts: who's in it. */
  members?: GroupMember[];
  /** Group texts: the name the team gave it, if any. */
  groupName?: string | null;
  number: string;
  name: string | null;
  status: "open" | "done";
  assignedTo: number | null;
  calls: ThreadCall[];
  messages: Text[];
  comments: Note[];
  /** Caller ID name from a lookup (CNAM). Show only when name (the contact) is null. */
  callerName?: string | null;
  /** Where the name we show for an unsaved number came from: the AI (voicemail / call) or caller ID. */
  nameGuess?: { name: string; source: "voicemail" | "call" | "caller-id" } | null;
  /** Texts scheduled to go out later (show as ghost bubbles). */
  scheduled?: ScheduledText[];
  /** Which line this conversation is on (the server says). */
  line?: "personal" | "team";
  /** 7.10.30: older history exists; pass olderBefore as ?before= to get the next page. */
  more?: boolean;
  olderBefore?: string | null;
  /** 9.11.2: a merged contact: all their numbers (one conversation), and the one to answer on. */
  numbers?: string[];
  replyNumber?: string;
}
export interface ScheduledText {
  id: number;
  number: string;
  body: string;
  media: Attachment[];
  /** ISO 8601 UTC with ms. */
  sendAt: string;
  userId: number | null;
  userName: string | null;
  contactName: string | null;
  status: "pending" | "sending" | "sent" | "failed" | "canceled";
  messageId: number | null;
  error: string | null;
  createdAt: string;
}
export interface LookupSettings {
  enabled: boolean;
}
export interface CallerLookupResult {
  number: string;
  enabled: boolean;
  isContact: boolean;
  callerName: string | null;
  carrier: string | null;
  lineType: string | null;
  lookedUpAt: string | null;
}
export interface ChatMessage {
  reactions: Reaction[];
  /** Written by the AI agent. */
  bot?: number | boolean;
  id: number;
  user_id: number;
  user_name: string;
  body: string;
  reply_to: number | null;
  reply_body: string | null;
  reply_user_name: string | null;
  created_at: string;
  /** Set once the author edits it. */
  edited_at?: string | null;
  /** Pictures / PDFs sent with it. */
  media?: Attachment[];
}
/** When a teammate last had Team chat open (server time, like created_at). */
export interface ChatRead {
  userId: number;
  name: string;
  readAt: string;
}
export interface Invite {
  id: number;
  name: string;
  email: string;
  role: "admin" | "member";
  invitedBy: string;
  expiresAt: string;
}

export type RouteAction =
  | { type: "team" }
  | { type: "user"; userId: number }
  | { type: "voicemail" }
  | { type: "message"; text: string }
  /** Dial an outside number (E.164) from the team number and connect the caller; not answered in 25s → voicemail. */
  | { type: "forward"; number: string };
export interface RingStrategy {
  /** "all" = everyone at once (default); "sequence" = one teammate at a time in `order`. */
  mode: "all" | "sequence";
  /** User ids, first rung first; teammates not listed ring after the listed ones. DND / no-login are skipped. */
  order: number[];
  /** Seconds per teammate in "sequence" mode, 10–60 (default 15). */
  stepSecs: number;
}
export interface MenuOption {
  digit: string;
  label: string;
  keywords: string[];
  action: RouteAction;
}
export interface PhoneSettings {
  greeting: { enabled: boolean; mode: "text" | "audio"; text: string; audioUrl: string | null };
  menu: { enabled: boolean; prompt: string; speech: boolean; options: MenuOption[]; fallback: RouteAction; tries?: number };
  /** Voice callers hear. */
  voice?: VoiceKey;
  voicemailGreeting: string;
  holdMusicUrl: string | null;
  hours: BusinessHours;
  recordingNotice: { enabled: boolean; text: string };
  autoReply: {
    missedCall: { enabled: boolean; text: string };
    voicemail: { enabled: boolean; text: string };
    afterHours: { enabled: boolean; text: string };
    cooldownHours: number;
  };
  /** Nobody answered in time / everyone on DND: { type: "voicemail" } (default) or { type: "forward", number }. Omit to keep. */
  noAnswer?: RouteAction;
  /** Omit to keep. Default { mode: "all", order: [], stepSecs: 15 }. */
  ringStrategy?: RingStrategy;
  calls: CallSettings;
}
export interface CallSettings {
  ringStrategy: "simultaneous" | "sequential";
  ringOrder: number[];
  ringSecondsEach: number;
  ringTimeoutSecs: number;
  overflow: RouteAction;
  recordCalls: boolean;
  transcribe: boolean;
  callerNameLookup: boolean;
  callWaiting: boolean;
  allowMonitorAll: boolean;
}
export interface QuoImportStatus {
  configured: boolean;
  running: boolean;
  phase: string;
  startedAt: string | null;
  finishedAt: string | null;
  canceled: boolean;
  counts: Record<"contacts" | "conversations" | "messages" | "media" | "calls" | "voicemails" | "recordings" | "transcripts" | "summaries" | "skippedGroups" | "errors", number> & { notes?: number; tasks?: number };
  lastError: string | null;
  notOnPlan: string[];
}
export interface UserPrefs {
  notify: { calls: boolean; missedCalls: boolean; voicemail: boolean; texts: "all" | "assigned" | "none"; mentions: boolean; teamChat: boolean; tasks: boolean };
  quietHours: { enabled: boolean; from: string; to: string; timezone: string };
  textSignature: { enabled: boolean; text: string };
  /** This teammate's own ringtone and call sounds; null = not chosen yet. */
  sounds: { ringtone: string; text: string; mention: string; volume: number; keypadTones: boolean; waitingBeep: boolean; ringback: boolean; hangupTone?: boolean } | null;
}
export type Weekday = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
export interface BusinessHours {
  enabled: boolean;
  timezone: string;
  days: Record<Weekday, { open: boolean; from: string; to: string }>;
  afterHours: RouteAction;
  closedDates: string[];
  /** 3.10.23: what after-hours callers hear first (instead of the daytime greeting), and their voicemail greeting. */
  greeting: { enabled: boolean; text: string };
  voicemailGreeting: string;
}

export interface ContactSummary {
  number: string;
  name: string;
  company: string | null;
  email: string | null;
  tags: string[];
  optedOut: boolean;
  notesCount: number;
  lastActivityAt: string | null;
  /** 6.11.2: numbers merged into this contact. */
  otherNumbers?: string[];
}
export interface ContactNote {
  id: number;
  userId: number;
  userName: string;
  body: string;
  replyTo: number | null;
  pinned: boolean;
  createdAt: string;
  updatedAt: string | null;
  reactions: Reaction[];
}
export interface ContactDetail {
  number: string;
  /** null when nobody has saved this number yet. */
  name: string | null;
  company: string | null;
  email: string | null;
  tags: string[];
  details: ContactDetails;
  optedOut: boolean;
  blocked: boolean;
  createdBy: number | null;
  createdAt: string | null;
  exists: boolean;
  notes: ContactNote[];
  stats: { calls: number; texts: number; lastActivityAt: string | null };
  /** 6.11.2: this number was merged into that contact (its own row stays: name, STOP). */
  mergedInto?: string | null;
  /** 6.11.2: the numbers merged into this contact. */
  otherNumbers?: string[];
}
export interface NotesProposal { number: string; name: string; added: string[]; updated: string[]; unsure: string[]; changes: string[]; error?: string }
export interface NotesJob {
  id: string;
  status: "running" | "ready" | "applying" | "applied" | "undone";
  startedAt: number;
  total: number;
  done: number;
  proposals: NotesProposal[];
  unchanged: number;
  failed: number;
  appliedNumbers: string[];
  skipped: string[];
  /** Ran on its own (after an update, hourly, or when a note was saved). */
  auto?: boolean;
}
export interface ContactChange {
  name: string;
  company?: string | null;
  email?: string | null;
  tags?: string[];
  details?: ContactDetails;
}
export interface LabeledValue { label: string; value: string }
export interface FamilyMember { name: string; relation: string; birthday: string; passportExp: string; ktn?: string; redress?: string; notes: string; gender?: "M" | "F" }
/** Customer profile: legal name, birthday, extra phones/emails, family members… ("" = not set). */
export interface ContactDetails {
  legalName: string;
  birthday: string;
  anniversary: string;
  passportExp: string;
  /** Known Traveler Number (TSA PreCheck / Global Entry) and Redress number. */
  ktn?: string;
  redress?: string;
  address: string;
  preferences: string;
  phones: LabeledValue[];
  emails: LabeledValue[];
  family: FamilyMember[];
  /** 3.10.23: "M" / "F", for the GDS DOCS line. */
  gender?: "M" | "F";
}
export type ActivityKind = "mention" | "note_mention" | "chat_mention" | "assigned" | "task_assigned" | "task_due" | "missed_call";
export interface ActivityItem {
  id: number;
  kind: ActivityKind;
  actorId: number | null;
  actorName: string | null;
  number: string | null;
  contactName: string | null;
  /** "comment:5", "note:3", "chat:9", "thread:+1415…", "task:4" */
  ref: string | null;
  body: string | null;
  createdAt: string;
  readAt: string | null;
}
export interface SearchHit {
  /** chat: Team chat · agent: your chat with Selena · internal: a call inside the team (its summary). number is "chat", "agent" or the call's key. */
  kind: "contact" | "text" | "comment" | "transcript" | "summary" | "note" | "chat" | "agent" | "internal";
  number: string;
  contactName: string | null;
  snippet: string;
  at: string;
  /** Anchor to scroll to: "sms:12", "call:<id>", "comment:5", "note:3", "contact:<number>". */
  ref: string;
  /** On the admin's personal line. */
  line?: "personal";
}
export interface Snippet {
  id: number;
  title: string;
  body: string;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
}
export interface BlockedNumber {
  number: string;
  reason: string | null;
  contactName: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
}
export interface Task {
  id: number;
  number: string | null;
  contactName: string | null;
  title: string;
  /** ISO 8601 (UTC). */
  dueAt: string | null;
  assigneeId: number | null;
  assigneeName: string | null;
  createdBy: number | null;
  createdByName: string | null;
  done: boolean;
  doneAt: string | null;
  remindedAt: string | null;
  createdAt: string;
  /** The call or text in the thread it's about ("sms:12", "call:<id>"); null = the whole conversation. */
  ref?: string | null;
  /** The AI assistant makes this call herself when it's due (not a to-do for the teammate). */
  aiCall?: { agent: string } | null;
}
export interface TaskChange {
  title?: string;
  /** ISO string or ms; null clears. */
  dueAt?: string | number | null;
  assigneeId?: number | null;
  done?: boolean;
}
export interface Analytics {
  days: 7 | 30;
  since: string;
  inbound: number;
  answered: number;
  missed: number;
  voicemail: number;
  outbound: number;
  textsIn: number;
  textsOut: number;
  /** 0–1, null with no inbound calls. */
  answerRate: number | null;
  byAgent: { userId: number; name: string; answered: number; outbound: number; textsSent: number; talkSecs: number }[];
  /** 24 counts of inbound calls by hour (shifted by tzOffsetMins). */
  byHour: number[];
  medianResponseMins: number | null;
}

export type SignInApproval = { id: string; device: string; choices: string[] };

export class ApiError extends Error {
  // 7.10.30: `data` is the server's whole answer (a refused text comes back as 502 with the failed `message`).
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
  }
}

/**
 * A request never hangs: after the time limit it fails (and the caller tries again). On iPhone, coming back to the
 * app could leave a request waiting forever on a dead connection, so the inbox never filled in until a reload.
 * Reads are also tried once more right away on a network failure (a stale connection after sleep).
 */
/** Which inbox the app is in: the team's line, or the admin's personal line (sent as X-Joy-Line on every request). */
let apiLine: "team" | "personal" = "team";
export function setApiLine(line: "team" | "personal") {
  apiLine = line;
}
/**
 * 7.10.30: the call on screen (its Telnyx session id), sent as X-Joy-Call on every /calls/current/* request. After
 * call waiting there are two calls and the server picked the newest one, so Hold, Transfer, Add, Pause rec. and the
 * listen-in mode acted on the wrong call. Set once by the app (main.ts) to read the phone's current call.
 */
let currentCallSession: () => string | null | undefined = () => null;
export function setCurrentCallSession(get: () => string | null | undefined) {
  currentCallSession = get;
}
function callHeader(path: string): Record<string, string> {
  if (!path.startsWith("/calls/current")) return {};
  const id = currentCallSession();
  return id ? { "X-Joy-Call": id } : {};
}
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // The time limit covers reading the answer too: a phone that sleeps mid-request can leave the body hanging
  // forever, and a send button waiting on it stayed grey for good (19.10.18 "texting Steven getting stuck").
  const attempt = async () => {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), method === "GET" ? 10_000 : 30_000);
    try {
      const res = await fetch(`/api${path}`, {
        method,
        credentials: "same-origin",
        headers: { ...(body === undefined ? {} : { "Content-Type": "application/json" }), ...(apiLine === "personal" ? { "X-Joy-Line": "personal" } : {}), ...callHeader(path) },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctl.signal,
      });
      const data: unknown = await res.json().catch((e: Error) => {
        if (ctl.signal.aborted) throw e;
        return {};
      });
      return { res, data };
    } catch (e) {
      throw ctl.signal.aborted ? new Error("The connection timed out. Please try again.") : e;
    } finally {
      clearTimeout(timer);
    }
  };
  let got: { res: Response; data: unknown };
  try {
    got = await attempt();
  } catch (e) {
    if (method !== "GET") throw e;
    got = await attempt();
  }
  const { res, data } = got;
  if (res.status === 403 && (data as { mfa?: MfaGate }).mfa) onMfaNeeded?.((data as { mfa: MfaGate }).mfa);
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? `Request failed (${res.status})`, data);
  return data as T;
}

/** "verify": enter the code / use Face ID; "setup": the team requires two-step sign-in and it isn't set up yet. */
export type MfaGate = "verify" | "setup";
let onMfaNeeded: ((gate: MfaGate) => void) | null = null;
/** Called when the server says this session still needs two-step sign-in (the app shows that screen). */
export function setMfaHandler(fn: (gate: MfaGate) => void) {
  onMfaNeeded = fn;
}

export interface TwoFactorStatus {
  required: boolean;
  authenticator: boolean;
  backupCodesLeft: number;
  passkeys: { id: string; name: string; createdAt: string; lastUsedAt: string | null }[];
  gate?: MfaGate | null;
}
export interface CallingGuards { international: boolean; countries: string[]; caribbean: boolean; perUserHour: number; lineHour: number; intlDay: number }
export interface SecuritySettings { require2fa: boolean; calling: CallingGuards }
export interface SecurityLogItem { id: number; kind: string; user_id: number | null; user_name: string | null; detail: string | null; created_at: string }
export interface EmailSettings { to: string[]; missedCalls: boolean; voicemails: boolean; texts: boolean; security: boolean; replyByEmail?: boolean }
export interface EmailReplyStatus { state: "off" | "connecting" | "watching" | "error"; error: string | null; lastReplyAt: string | null; handled: number }

const t = (number: string) => `/threads/${encodeURIComponent(number)}`;
const k = (number: string) => `/contacts/${encodeURIComponent(number)}`;
const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
  const s = new URLSearchParams();
  for (const [key, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== false) s.set(key, v === true ? "1" : String(v));
  const out = s.toString();
  return out ? `?${out}` : "";
};

export const api = {
  login: (email: string, password: string) => request<{ user: User; mfa: MfaGate | null }>("POST", "/auth/login", { email, password }),
  // ---- two-step sign-in & Face ID ----
  twoFactor: () => request<TwoFactorStatus>("GET", "/auth/2fa"),
  verifyTwoFactor: (code: string, remember: boolean) => request<{ ok: true }>("POST", "/auth/2fa/verify", { code, remember }),
  setupTwoFactor: () => request<{ secret: string; otpauthUrl: string; qr: string }>("POST", "/auth/2fa/setup"),
  enableTwoFactor: (code: string) => request<TwoFactorStatus & { backupCodes: string[] }>("POST", "/auth/2fa/enable", { code }),
  disableTwoFactor: (code: string) => request<TwoFactorStatus>("POST", "/auth/2fa/disable", { code }),
  newBackupCodes: (code: string) => request<{ backupCodes: string[] }>("POST", "/auth/2fa/backup-codes", { code }),
  passkeyRegisterOptions: () => request<{ flow: string; options: any }>("POST", "/auth/passkey/register/options"),
  passkeyRegister: (flow: string, response: unknown, name?: string) => request<TwoFactorStatus>("POST", "/auth/passkey/register", { flow, response, name }),
  deletePasskey: (id: string) => request<TwoFactorStatus>("DELETE", `/auth/passkeys/${encodeURIComponent(id)}`),
  approvalRequest: () => request<{ id: string; number: string; expiresInSecs: number }>("POST", "/auth/2fa/approval"),
  approvalStatus: (id: string) => request<{ state: "waiting" | "approved" | "denied" | "expired" }>("GET", `/auth/2fa/approval/${encodeURIComponent(id)}`),
  /** Sign-ins waiting for this teammate's OK. No real number: three choices, one of them shown on that device. */
  approvals: async (): Promise<{ approvals: SignInApproval[] }> => {
    const r = await request<{ approvals: SignInApproval[] } | SignInApproval[]>("GET", "/auth/2fa/approvals");
    return { approvals: Array.isArray(r) ? r : r.approvals ?? [] };
  },
  /** Approve by picking the number shown on the other device (a wrong pick counts as deny), or deny. */
  answerApproval: (id: string, answer: { number: string } | "deny") =>
    answer === "deny"
      ? request<{ ok: true }>("POST", `/auth/2fa/approvals/${encodeURIComponent(id)}/deny`)
      : request<{ ok: true }>("POST", `/auth/2fa/approvals/${encodeURIComponent(id)}/approve`, { number: answer.number }),
  passkeyLoginOptions: () => request<{ flow: string; options: any }>("POST", "/auth/passkey/login/options"),
  passkeyLogin: (flow: string, response: unknown) => request<{ ok: true }>("POST", "/auth/passkey/login", { flow, response }),
  security: () => request<{ settings: SecuritySettings; countries: Record<string, string>; log: SecurityLogItem[] }>("GET", "/security"),
  saveSecurity: (s: Partial<SecuritySettings>) => request<{ settings: SecuritySettings }>("PUT", "/security", s),
  resetTwoFactor: (userId: number) => request<{ ok: true }>("POST", `/team/${userId}/reset-2fa`),
  emailSettings: () => request<{ settings: EmailSettings; configured: boolean; via?: string | null; lastError: string | null; replies?: EmailReplyStatus | null }>("GET", "/settings/email"),
  saveEmailSettings: (s: Partial<EmailSettings>) => request<{ settings: EmailSettings; configured: boolean; via?: string | null; lastError: string | null; replies?: EmailReplyStatus | null }>("PUT", "/settings/email", s),
  testEmail: () => request<{ ok: true }>("POST", "/settings/email/test"),
  logout: () => request("POST", "/auth/logout"),
  me: () => request<{ user: User; teamNumber: string; vapidPublicKey: string | null; activityUnread: number; mfa?: MfaGate }>("GET", "/me"),
  voiceToken: () => request<{ token: string; sipUsername: string }>("GET", "/voice-token"),
  /** Diagnostics: what this device saw at the start of a call (admins read it at /api/diag/calls). */
  diagCall: (report: Record<string, unknown>) => request<void>("POST", "/diag/call", report),
  subscribePush: (subscription: PushSubscriptionJSON) => request("POST", "/push/subscribe", { subscription }),
  unsubscribePush: (endpoint: string) => request("POST", "/push/unsubscribe", { endpoint }),
  inbox: () =>
    request<{ conversations: Conversation[]; live: LiveCall[]; chat: { unread: number; last: { body: string; user_name: string; created_at: string } | null }; activity: { unread: number };
      personal?: { number: string | null; unread: number } | null; team?: { unread: number }; agent?: { unread: number }; agentName?: string; agentMan?: boolean; agents?: { team: { name: string; man: boolean }; personal?: { name: string; man: boolean } }; hours?: { enabled: boolean; open: boolean }; storageWarn?: StorageInfo | null; takeover?: PendingTakeover | null; liveBrowser?: { token: string | null; where: string; by?: string | null; starting?: "queued" | "opening" } | null }>("GET", "/inbox"),
  /** "Take over my browser": the step Selena is waiting on, its live screen, your input, and handing it back. */
  takeover: (token: string) => request<PendingTakeover & { url: string; agentName: string }>("GET", `/takeover/${encodeURIComponent(token)}`),
  takeoverInput: (token: string, input: Record<string, unknown>) => request<{ ok: boolean; url?: string; title?: string }>("POST", `/takeover/${encodeURIComponent(token)}/input`, { input }),
  /** 3.11.3: take over her browser while watching (she waits until you tap Done). */
  takeoverControl: (token: string) => request<{ ok: boolean }>("POST", `/takeover/${encodeURIComponent(token)}/control`, {}),
  takeoverDone: (token: string, stop = false) => request<{ ok: boolean }>("POST", `/takeover/${encodeURIComponent(token)}/done`, stop ? { stop: true } : {}),
  storage: () => request<{ storage: StorageInfo | null; warnPct: number }>("GET", "/storage"),
  thread: (number: string) => request<Thread>("GET", t(number)),
  /** 7.10.30: the next older page of a conversation (calls, texts and notes before that time). */
  threadBefore: (number: string, before: string) => request<Thread>("GET", `${t(number)}?before=${encodeURIComponent(before)}`),
  markRead: (number: string) => request("POST", `${t(number)}/read`),
  markUnread: (number: string) => request("POST", `${t(number)}/unread`),
  editComment: (number: string, id: number, body: string) => request<{ id: number; body: string }>("PATCH", `${t(number)}/comments/${id}`, { body }),
  editChat: (id: number, body: string) => request<{ id: number; body: string }>("PATCH", `/chat/${id}`, { body }),
  deleteComment: (number: string, id: number) => request<{ deleted: number }>("DELETE", `${t(number)}/comments/${id}`),
  deleteChat: (id: number) => request<{ deleted: number }>("DELETE", `/chat/${id}`),
  readAll: () => request<{ marked: number }>("POST", "/inbox/read-all"),
  /** Tells teammates you're typing in a conversation ("chat" or "t:<number>"); stop when sent or cleared. */
  typing: (where: string, mode: "chat" | "text" | "comment", stop = false) => request<void>("POST", "/typing", { where, mode, stop }),
  clearActivity: () => request<{ cleared: number }>("POST", "/activity/clear"),
  deleteThread: (number: string) => request<{ calls: number; messages: number; comments: number }>("DELETE", t(number)),
  /** Your calls with Selena / Steven or a teammate, on this line (admins). */
  renameGroup: (key: string, name: string) => request<{ key: string; name: string | null }>("PUT", `/groups/${encodeURIComponent(key)}/name`, { name }),
  deleteInternal: (key: string) => request<{ calls: number; messages: number; comments: number }>("DELETE", `/internal/${encodeURIComponent(key)}`),
  setConversation: (number: string, change: { status?: "open" | "done"; assignedTo?: number | null }) =>
    request<{ status: "open" | "done"; assignedTo: number | null }>("PUT", `${t(number)}/conversation`, change),
  /** "Ask Selena" in a conversation: posted as a note; she answers under it. */
  askSelena: (number: string, body: string) => request<{ id: number }>("POST", `${t(number)}/ask-selena`, { body }),
  comment: (number: string, body: string, replyTo: number | null, anchor: string | null = null, media: Attachment[] = []) =>
    request<{ id: number }>("POST", `${t(number)}/comments`, { body, replyTo, anchor, ...(media.length ? { media } : {}) }),
  react: (kind: "sms" | "comment" | "chat" | "note", id: number, emoji: string) => request<{ added: boolean }>("POST", "/reactions", { kind, id, emoji }),
  text: (number: string, body: string, media: Attachment[] = []) => request<{ message: Text }>("POST", `${t(number)}/messages`, { body, media }),
  /** `forSelena`: her chat and Team chat (documents too, up to 15 MB); `type` overrides the browser's guess. */
  async uploadAttachment(file: Blob, name: string, forSelena = false, type?: string): Promise<Attachment> {
    // Time-limited like every request: a stuck upload used to keep every send button grey (even Ask Steven's).
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 90_000);
    let res: Response, data: unknown;
    try {
      res = await fetch(`/api/attachments?name=${encodeURIComponent(name)}${forSelena ? "&for=selena" : ""}`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": type ?? file.type }, body: file, signal: ctl.signal });
      data = await res.json().catch(() => ({}));
    } catch (e) {
      throw ctl.signal.aborted ? new Error(`${name} took too long to upload. Please try again.`) : e;
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? (res.status === 413 ? `That file is too big (${forSelena ? 15 : 5} MB max)` : "Upload failed"));
    return data as Attachment;
  },
  setContact: (number: string, name: string) =>
    request<{ number: string; name: string | null }>("PUT", `/contacts/${encodeURIComponent(number)}`, { name }),
  /** A teammate call's notification was tapped: ring this app (again). */
  intercomPickup: (callId: string) => request("POST", `/intercom/${encodeURIComponent(callId)}/pickup`),
  /** Selena's user id (to call her from the app). */
  agentInfo: () => request<{ id: number; name: string }>("GET", "/agent/info"),
  pickup: (callId: string) => request("POST", `/calls/${encodeURIComponent(callId)}/pickup`),
  hold: (on: boolean) => request("POST", "/calls/current/hold", { on }),
  pauseRecording: (paused: boolean) => request<{ ok: true; paused: boolean }>("POST", "/calls/current/recording", { paused }),
  // ---- advanced calls (CALLS_API.md) ----
  /** Admins: listen in / whisper / barge. Arm auto-answer for this call's session id BEFORE calling (see CALLS_API.md). */
  monitorCall: (callId: string, mode: MonitorMode = "listen") =>
    request<{ ok: true; mode: MonitorMode }>("POST", `/calls/${encodeURIComponent(callId)}/monitor`, { mode }),
  /** Switch my listen-in leg between listen / whisper / barge. */
  setMonitorMode: (mode: MonitorMode) => request<{ ok: true; mode: MonitorMode }>("POST", "/calls/current/monitor-mode", { mode }),
  /** 3-way call: add a teammate or outside number to my call. */
  addParticipant: (target: CallTarget) => request<{ ok: true }>("POST", "/calls/current/participants", target),
  removeParticipant: (participantId: string) => request<{ ok: true }>("DELETE", `/calls/current/participants/${encodeURIComponent(participantId)}`),
  /** Warm transfer: caller on hold while I talk with the target; then complete or cancel. */
  consult: (target: CallTarget) => request<{ ok: true }>("POST", "/calls/current/consult", target),
  completeConsult: () => request<{ ok: true }>("POST", "/calls/current/consult/complete"),
  cancelConsult: () => request<{ ok: true }>("POST", "/calls/current/consult/cancel"),
  /** "Ring my cell too" (E.164 / 10-digit US, or null to turn off) and/or my own voicemail greeting (null = team's). */
  updateMyCallSettings: (body: { forwardNumber?: string | null; forwardMode?: "never" | "always" | "dnd"; vmGreeting?: string | null;
    callVia?: "app" | "cell"; cellCallerId?: "customer" | "team"; cellText?: boolean }) =>
    request<{ user: User }>("PATCH", "/me", body),
  /** "Connect calls via my cell": your cell rings first, then the customer. */
  /** AI call: the AI phones `to` and asks what's in `request`; the results land in the conversation. */
  aiCall: (to: string, task: { hotel?: string; request: string; details?: string; book?: boolean; ext?: string }) => request<{ callId: string }>("POST", "/calls/ai", { to, ...task }),
  /** Your private chat with the AI agent. */
  agentChat: () => request<{ messages: AgentMessage[]; thinking: boolean; mode?: "business" | "personal" }>("GET", "/agent"),
  /** Selena as your personal assistant (Business / Personal mode, your own details). Private to you. */
  /** Settings → Phone numbers (admins). */
  numbers: () => request<NumbersBody>("GET", "/numbers"),
  connectNumber: (id: string) => request<{ ok: true; texts: boolean }>("POST", `/numbers/${encodeURIComponent(id)}/connect`),
  setOfficeNumber: (number: string) => request<{ office: string; previous: string }>("POST", "/numbers/office", { number }),
  setPersonalNumber: (number: string) => request<{ personal: string }>("POST", "/numbers/personal", { number }),
  /** Just before the app dials from the personal inbox: that call goes out from the personal number. */
  personalDial: (to: string) => request<{ ok: true }>("POST", "/calls/personal-dial", { to }),
  // 13.10.30 Settings → Browser → Compare browsers
  identityCheck: () => request<{ checks: { label: string; ok: boolean; detail: string }[] }>("POST", "/agent/identity-check"),
  browserTest: () => request<{ state: BenchState; tasks: BenchTask[] }>("GET", "/agent/browser-test"),
  startBrowserTest: () => request<{ state: BenchState; tasks: BenchTask[] }>("POST", "/agent/browser-test"),
  stopBrowserTest: () => request<{ stopped: boolean; state: BenchState }>("DELETE", "/agent/browser-test"),
  saveBrowserTestTasks: (body: { tasks?: BenchTask[]; reset?: boolean }) => request<{ tasks: BenchTask[] }>("PUT", "/agent/browser-test/tasks", body),
  agentPersonal: () => request<{ settings: PaSettings; teamNumber: string; allowed: boolean }>("GET", "/agent/personal"),
  saveAgentPersonal: (settings: Partial<PaSettings>) => request<{ settings: PaSettings; teamNumber: string }>("PUT", "/agent/personal", { settings }),
  agentSend: (body: string, media: Attachment[] = [], replyTo: number | null = null) => request<{ ok: true }>("POST", "/agent", { body, media, replyTo }),
  aiCalls: () => request<{ calls: AiAgentCall[] }>("GET", "/calls/ai"),
  aiCallStop: (callId: string) => request<{ ok: true }>("POST", `/calls/${encodeURIComponent(callId)}/ai-stop`),
  callViaCell: (to: string) => request<{ callId: string; cell: string | null }>("POST", "/calls/via-cell", { to }),
  appPasswords: () => request<{ passwords: { id: number; label: string; createdAt: string; lastUsedAt: string | null }[] }>("GET", "/me/app-passwords"),
  newAppPassword: (label?: string) => request<{ password: string; label: string }>("POST", "/me/app-passwords", { label }),
  deleteAppPassword: (id: number) => request<{ ok: true }>("DELETE", `/me/app-passwords/${id}`),
  setDnd: (dnd: boolean) => request<{ user: User }>("PATCH", "/me", { dnd }),
  transfer: (userId: number) => request("POST", "/calls/current/transfer", { userId }),
  /** Blind transfer to a teammate or an outside number. */
  /** Hand the caller to Selena (she takes over; `note`: what she should do). */
  transferToSelena: (note: string) => request<{ ok: true }>("POST", "/calls/current/transfer", { selena: true, note }),
  transferTo: (target: CallTarget) => request<{ ok: true }>("POST", "/calls/current/transfer", { ...target, mode: "cold" }),
  prefs: () => request<{ prefs: UserPrefs; defaults: UserPrefs; alertsLocked?: boolean; minVolume?: number }>("GET", "/me/prefs"),
  quoStatus: () => request<{ status: QuoImportStatus }>("GET", "/import/quo"),
  quoNumbers: () => request<{ numbers: { id: string; number: string; name: string | null }[] }>("GET", "/import/quo/numbers"),
  quoStart: (body: { phoneNumberIds: string[]; since: string | null; recordings: boolean; markOldDone: boolean }) =>
    request<{ status: QuoImportStatus }>("POST", "/import/quo/start", body),
  quoCancel: () => request<{ status: QuoImportStatus }>("POST", "/import/quo/cancel"),
  diagRoute: () => request<{ serverRegion: string | null; loginsSite: string | null; appSite: string | null; errors: string[]; ownSite?: boolean }>("GET", "/diag/route"),
  rcsSettings: () => request<{ rcs: { enabled: boolean; agentId: string; messagingProfileId: string }; envProfile: boolean }>("GET", "/settings/rcs"),
  saveRcsSettings: (body: { enabled: boolean; agentId: string; messagingProfileId: string }) =>
    request<{ rcs: { enabled: boolean; agentId: string; messagingProfileId: string }; envProfile: boolean }>("PUT", "/settings/rcs", body),
  teamMediaSites: () => request<{ sites: string[]; users: Record<number, string> }>("GET", "/team/media-sites"),
  setUserMediaSite: (id: number, site: string | null) => request<{ site: string | null }>("PUT", `/team/users/${id}/media-site`, { site }),
  diagnostics: () => request<{ serverRegion: string | null; telnyxApiMs: number | null; error: string | null }>("GET", "/diagnostics"),
  savePrefs: (prefs: Partial<UserPrefs>) => request<{ prefs: UserPrefs; alertsLocked?: boolean; minVolume?: number }>("PUT", "/me/prefs", prefs),
  alertsLock: () => request<{ locked: boolean }>("GET", "/settings/alerts-lock"),
  setAlertsLock: (locked: boolean) => request<{ locked: boolean }>("PUT", "/settings/alerts-lock", { locked }),
  chat: () => request<{ messages: ChatMessage[]; reads?: ChatRead[] }>("GET", "/chat"),
  /** 7.10.30: Team chat messages older than `before` (a message id), oldest first, like chat(). */
  chatBefore: (before: number) => request<{ messages: ChatMessage[]; reads?: ChatRead[] }>("GET", `/chat${qs({ before })}`),
  sendChat: (body: string, replyTo: number | null, media: Attachment[] = []) => request<{ id: number }>("POST", "/chat", { body, replyTo, media }),
  chatRead: () => request("POST", "/chat/read"),
  agentRead: () => request("POST", "/agent/read"),
  team: () => request<{ users: User[]; invites: Invite[]; alertsLocked?: boolean }>("GET", "/team"),
  /** Everyone's status; lastActiveAt only comes back for admins. */
  presence: () => request<{ presence: Presence[] }>("GET", "/presence"),
  /** "I'm using the app" (keeps you green). */
  imActive: () => request<{ ok: true }>("POST", "/presence"),
  deleteRecording: (id: number) => request<{ ok: true; telnyx: boolean }>("DELETE", `/recordings/${id}`),
  setViewing: (number: string | null) => request<{ ok: true }>("POST", "/presence/viewing", { number }),
  invite: (name: string, email: string, role: "admin" | "member") => request<{ url: string }>("POST", "/team/invites", { name, email, role }),
  revokeInvite: (id: number) => request("DELETE", `/team/invites/${id}`),
  setRole: (id: number, role: "admin" | "member") => request("PATCH", `/team/users/${id}`, { role }),
  removeUser: (id: number) => request("DELETE", `/team/users/${id}`),
  setMyName: (name: string) => request<{ user: User }>("PATCH", "/me", { name }),
  updateUser: (id: number, change: { name?: string; role?: "admin" | "member" }) => request<{ user: User }>("PATCH", `/team/users/${id}`, change),
  setUserPassword: (id: number, password: string) => request("POST", `/team/users/${id}/password`, { password }),
  changePassword: (current: string, password: string) => request("POST", "/me/password", { current, password }),
  resetLink: (userId: number) => request<{ url: string }>("POST", `/team/users/${userId}/reset`),
  getReset: (token: string) => request<{ name: string; email: string }>("GET", `/resets/${encodeURIComponent(token)}`),
  useReset: (token: string, password: string) => request<{ user: User }>("POST", `/resets/${encodeURIComponent(token)}`, { password }),
  getInvite: (token: string) => request<{ name: string; email: string }>("GET", `/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, name: string, password: string) =>
    request<{ user: User }>("POST", `/invites/${encodeURIComponent(token)}/accept`, { name, password }),
  phoneSettings: () => request<{ settings: PhoneSettings; preview: string; voiceProblem?: { voice: string; reason: string; at: string } | null }>("GET", "/settings/phone"),
  /** The text read in the phone voice (MP3). */
  async phoneSample(text: string, voice: string | undefined): Promise<Blob> {
    const res = await fetch("/api/settings/phone/sample", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, voice }) });
    if (!res.ok) throw new ApiError(res.status, ((await res.json().catch(() => ({}))) as { error?: string }).error ?? "Couldn't play the sample");
    return res.blob();
  },
  /** The AI agent's call voice. */
  /** Selena → Settings. */
  /** Delete a message in your chat with Selena, or the whole chat. */
  deleteAgentMessage: (id: number) => request<{ deleted: number }>("DELETE", `/agent/messages/${id}`),
  clearAgentChat: () => request<{ deleted: number }>("DELETE", "/agent/messages"),
  /** A finished call's audio report from this device (Settings › Call audio log, for admins). */
  audioReport: (r: Record<string, unknown>) => request<{ ok: boolean }>("POST", "/diag/audio", r),
  audioLog: () => request<{ reports: Record<string, any>[] }>("GET", "/diag/audio"),
  agentSettings: () => request<SelenaSettingsBody>("GET", "/agent/settings"),
  /** Settings → Try models (admins): Telnyx's chat models, and one model's answer to a test question. */
  agentModels: () => request<{ current: string | null; models: string[]; thinking: boolean }>("GET", "/agent/models"),
  agentTry: (model: string, question: string, thinking: boolean, web: boolean) =>
    request<{ model: string; ms: number; reply?: string; error?: string; searchMs?: number | null }>("POST", "/agent/try", { model, question, thinking, web }),
  saveAgentSettings: (settings: Partial<SelenaSettings>) => request<SelenaSettingsBody>("PUT", "/agent/settings", { settings }),
  async agentVoiceSample(voice: string): Promise<Blob> {
    const res = await fetch("/api/agent/voice/sample", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ voice }) });
    if (!res.ok) throw new ApiError(res.status, ((await res.json().catch(() => ({}))) as { error?: string }).error ?? "Couldn't play the sample");
    return res.blob();
  },
  savePhoneSettings: (settings: PhoneSettings) => request<{ settings: PhoneSettings; preview: string }>("PUT", "/settings/phone", { settings }),
  billing: (month: string, refresh = false) => request<Billing>("GET", `/billing?month=${month}${refresh ? "&refresh=1" : ""}`),
  portingCheck: (numbers: string[]) =>
    request<{ results: { phone_number: string; portable: boolean; not_portable_reason: string; fast_portable: boolean }[] }>("POST", "/porting/check", { numbers }),
  mediaSite: () => request<{ site: string | null; sites: string[] }>("GET", "/settings/media-site"),
  hdVoice: () => request<{ codecs: string[] | null; hd: boolean | null }>("GET", "/settings/hd-voice"),
  setMediaSite: (site: string) => request<{ site: string; appUpdated: boolean }>("PUT", "/settings/media-site", { site }),
  async uploadAudio(file: File): Promise<{ url: string }> {
    const type = /\.wav$/i.test(file.name) || /wav/.test(file.type) ? "audio/wav" : "audio/mpeg";
    const res = await fetch("/api/settings/media", { method: "POST", credentials: "same-origin", headers: { "Content-Type": type }, body: file });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? "Upload failed");
    return data as { url: string };
  },

  // ---- contacts & notes ----
  contacts: (q = "") => request<ContactSummary[]>("GET", `/contacts${qs({ q: q || undefined })}`),
  contact: (number: string) => request<ContactDetail>("GET", k(number)),
  myEmoji: () => request<{ emoji: string[] }>("GET", "/me/emoji"),
  /** 10.10.21: a typed blurb (travelers, DOBs, TSA numbers…) organized into the profile (not saved). */
  /** 1.10.26: notes → travel profiles (admins). */
  notesJob: () => request<{ job: NotesJob | null }>("GET", "/contacts/notes-to-profiles"),
  notesJobStart: () => request<{ job: NotesJob }>("POST", "/contacts/notes-to-profiles/start"),
  notesJobApply: (numbers: string[]) => request<{ applied: number; skipped: string[]; job: NotesJob }>("POST", "/contacts/notes-to-profiles/apply", { numbers }),
  notesJobUndo: () => request<{ undone: number; kept: string[]; job: NotesJob }>("POST", "/contacts/notes-to-profiles/undo"),
  organizeDetails: (number: string, body: { text: string; details: ContactDetails; name: string }) =>
    request<{ details: ContactDetails; added: string[]; updated: string[]; unsure: string[] }>("POST", `/contacts/${encodeURIComponent(number)}/details/organize`, body),
  saveContact: (number: string, change: ContactChange) =>
    request<{ number: string; name: string | null; company: string | null; email: string | null; tags: string[] }>("PUT", k(number), change),
  deleteContact: (number: string) => request<{ ok: true }>("DELETE", k(number)),
  /** 6.11.2: `other` becomes one of this contact's numbers ("theirs": keep the other contact's name). */
  mergeContact: (number: string, other: string, keepName: "mine" | "theirs" = "mine") =>
    request<{ number: string; name: string; otherNumbers: string[] }>("POST", `${k(number)}/merge`, { other, keepName }),
  unmergeContact: (number: string) => request<{ ok: true; number: string; from: string }>("POST", `${k(number)}/unmerge`, {}),
  addContactNote: (number: string, body: string, replyTo: number | null = null) =>
    request<{ id: number }>("POST", `${k(number)}/notes`, { body, replyTo }),
  updateContactNote: (number: string, id: number, change: { pinned?: boolean; body?: string }) =>
    request<{ id: number; body: string; pinned: boolean; updatedAt: string | null }>("PATCH", `${k(number)}/notes/${id}`, change),
  deleteContactNote: (number: string, id: number) => request<{ ok: true }>("DELETE", `${k(number)}/notes/${id}`),
  reactToNote: (id: number, emoji: string) => request<{ added: boolean }>("POST", "/reactions", { kind: "note", id, emoji }),
  /** Link target for a download (the browser sends the session cookie). 7.10.30: the personal line's own contacts
   * there (a plain link can't send X-Joy-Line, so the line goes in the query). */
  contactsCsvUrl: () => (apiLine === "personal" ? "/api/contacts.csv?line=personal" : "/api/contacts.csv"),
  async importContacts(csv: Blob | string): Promise<{ created: number; updated: number; skipped: number }> {
    // 7.10.30: on the personal line, into the personal contacts (they all went into the team's before).
    const personal = apiLine === "personal";
    const res = await fetch(personal ? "/api/contacts/import?line=personal" : "/api/contacts/import", { method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "text/csv", ...(personal ? { "X-Joy-Line": "personal" } : {}) }, body: csv });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? "Import failed");
    return data as { created: number; updated: number; skipped: number };
  },

  // ---- activity feed ----
  activity: () => request<{ items: ActivityItem[]; unread: number }>("GET", "/activity"),
  readActivity: (ids?: number[]) => request<{ unread: number }>("POST", "/activity/read", ids ? { ids } : {}),

  // ---- inbox pins ----
  pinThread: (number: string) => request<{ pinned: true }>("PUT", `${t(number)}/pin`),
  unpinThread: (number: string) => request<{ pinned: false }>("DELETE", `${t(number)}/pin`),

  // ---- search ----
  search: (q: string) => request<SearchHit[]>("GET", `/search${qs({ q })}`),

  // ---- saved replies ----
  snippets: () => request<{ snippets: Snippet[] }>("GET", "/snippets"),
  createSnippet: (title: string, body: string) => request<{ snippet: Snippet }>("POST", "/snippets", { title, body }),
  updateSnippet: (id: number, change: { title?: string; body?: string }) => request<{ snippet: Snippet }>("PATCH", `/snippets/${id}`, change),
  deleteSnippet: (id: number) => request<{ ok: true }>("DELETE", `/snippets/${id}`),

  // ---- blocked numbers ----
  blocked: () => request<{ blocked: BlockedNumber[] }>("GET", "/blocked"),
  block: (number: string, reason?: string) =>
    request<{ number: string; blocked: true; reason: string | null }>("PUT", `/blocked/${encodeURIComponent(number)}`, { reason }),
  unblock: (number: string) => request<{ number: string; blocked: false }>("DELETE", `/blocked/${encodeURIComponent(number)}`),

  // ---- texts ----
  retryText: (messageId: number) => request<{ message: Text }>("POST", `/messages/${messageId}/retry`),

  // ---- scheduled texts ----
  scheduleText: (number: string, body: string, sendAt: string | number, media: Attachment[] = []) =>
    request<{ scheduled: ScheduledText }>("POST", `${t(number)}/scheduled`, { body, media, sendAt }),
  threadScheduled: (number: string) => request<{ scheduled: ScheduledText[] }>("GET", `${t(number)}/scheduled`),
  scheduled: () => request<{ scheduled: ScheduledText[] }>("GET", "/scheduled"),
  updateScheduled: (id: number, change: { body?: string; sendAt?: string | number }) =>
    request<{ scheduled: ScheduledText }>("PATCH", `/scheduled/${id}`, change),
  cancelScheduled: (id: number) => request<{ scheduled: ScheduledText }>("DELETE", `/scheduled/${id}`),

  // ---- caller name lookup (CNAM) ----
  lookupSettings: () => request<{ settings: LookupSettings }>("GET", "/settings/lookup"),
  saveLookupSettings: (settings: LookupSettings) => request<{ settings: LookupSettings }>("PUT", "/settings/lookup", { settings }),
  lookupCaller: (number: string) => request<CallerLookupResult>("GET", `/lookup/${encodeURIComponent(number)}`),

  // ---- follow-up tasks ----
  tasks: (filter: { mine?: boolean; open?: boolean; number?: string } = {}) =>
    request<{ tasks: Task[] }>("GET", `/tasks${qs({ mine: filter.mine, open: filter.open, number: filter.number })}`),
  createTask: (task: { title: string; number?: string | null; ref?: string | null; dueAt?: string | number | null; assigneeId?: number | null }) =>
    request<{ task: Task }>("POST", "/tasks", task),
  updateTask: (id: number, change: TaskChange) => request<{ task: Task }>("PATCH", `/tasks/${id}`, change),
  deleteTask: (id: number) => request<{ ok: true }>("DELETE", `/tasks/${id}`),
  /** Busy: the AI assistant takes this follow-up (and it's ticked off). */
  /** An AI call's live transcript (just that, while she's on the call). */
  callAiLog: (id: string) => request<{ id: string; status: string; on_hold: number; aiLog: { role: "ai" | "them"; text: string }[] | null; liveLog?: LiveLine[] | null }>("GET", `/calls/${encodeURIComponent(id)}/ai-log`),
  /** A conversation inside the team: calls with a teammate ("tm:1-2") or with the AI assistant ("ai:1"). */
  internal: (key: string) => request<InternalThread>("GET", `/internal/${encodeURIComponent(key)}`),
  delegateTask: (id: number) => request<{ ok: true; agent: string; where: "thread" | "chat" }>("POST", `/tasks/${id}/delegate`),

  // ---- analytics ----
  analytics: (days: 7 | 30 = 7, tzOffsetMins = -new Date().getTimezoneOffset()) =>
    request<Analytics>("GET", `/analytics${qs({ days, tzOffsetMins })}`),
};

export interface Presence {
  id: number;
  /** online = used the app in the last minute (green) · idle = open but untouched (yellow) · dnd (red) · offline (grey) */
  status: "online" | "idle" | "dnd" | "offline";
  /** Admins only. */
  lastActiveAt?: string | null;
  /** Admins only: when the current status started. */
  since?: string | null;
  /** The conversation (number) this teammate has open right now. */
  viewing?: string | null;
}

export interface InternalThread {
  key: string;
  name: string;
  ai: boolean;
  userId: number | null;
  calls: { id: string; status: string; startedAt: string; answeredAt: string | null; endedAt: string | null; summary: string | null; callerName: string | null; mine: boolean;
    /** 3.10.23: the call's recording(s). */
    recordings?: { id: number; durationSecs: number | null }[] }[];
}
